import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Map<number, number>} */
  #latestCartUpdateRequestIdsByLine = new Map();
  /** @type {Set<number>} */
  #wishlistMoveLines = new Set();
  #lastWishlistTouchLine = 0;
  #lastWishlistTouchAt = 0;
  /** @type {{ x: number, y: number } | null} */
  #wishlistTouchStart = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleCartDrawerWishlistClick);
    this.addEventListener('touchstart', this.#handleCartDrawerWishlistTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleCartDrawerWishlistClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleCartDrawerWishlistClick);
    this.removeEventListener('touchstart', this.#handleCartDrawerWishlistTouchStart);
    this.removeEventListener('touchend', this.#handleCartDrawerWishlistClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(/** @type {QuantitySelectorUpdateEvent} */ event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#updateVisibleCartCount(line, quantity);

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#updateVisibleCartCount(line, 0);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Adds the cart drawer item to the shared wishlist flow and removes it from the cart only after success.
   * @param {number} line - The line item index.
   */
  async onCartDrawerMoveToWishlist(line) {
    if (this.#wishlistMoveLines.has(line)) return;

    const cartItemRow = this.refs.cartItemRows[line - 1];

    if (!(cartItemRow instanceof HTMLElement) || !cartItemRow.closest('cart-drawer-component')) {
      return;
    }

    this.#wishlistMoveLines.add(line);
    let addedToWishlist = false;
    try {
      addedToWishlist = await this.#ensureCartDrawerItemInWishlist(cartItemRow);
    } finally {
      if (!addedToWishlist) {
        this.#wishlistMoveLines.delete(line);
      }
    }

    if (!addedToWishlist) return;

    this.onLineItemRemove(line);
    window.setTimeout(() => {
      this.#wishlistMoveLines.delete(line);
    }, 3000);
  }

  /**
   * Uses event delegation so Move to Wishlist continues working after AJAX cart drawer re-renders.
   * @param {Event} event
   */
  #handleCartDrawerWishlistClick = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element)) return;

    const wishlistButton = event.target.closest('.wishlist-product');
    if (!(wishlistButton instanceof HTMLButtonElement) || !this.contains(wishlistButton)) return;

    const line = Number(wishlistButton.dataset.cartLine || '');
    if (!Number.isFinite(line) || line <= 0) return;

    if (
      typeof TouchEvent !== 'undefined' &&
      event instanceof TouchEvent &&
      event.type === 'touchend' &&
      this.#didWishlistTouchMove(event)
    ) {
      this.#wishlistTouchStart = null;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'click' && this.#lastWishlistTouchLine === line && Date.now() - this.#lastWishlistTouchAt < 700) {
      return;
    }

    if (event.type === 'touchend') {
      this.#lastWishlistTouchLine = line;
      this.#lastWishlistTouchAt = Date.now();
      this.#wishlistTouchStart = null;
    }

    void this.onCartDrawerMoveToWishlist(line);
  };

  /**
   * @param {TouchEvent} event
   */
  #handleCartDrawerWishlistTouchStart = (event) => {
    if (!(event.target instanceof Element)) return;
    const wishlistButton = event.target.closest('.wishlist-product');
    if (!(wishlistButton instanceof HTMLButtonElement) || !this.contains(wishlistButton)) return;

    const touch = event.touches[0];
    this.#wishlistTouchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };

  /**
   * @param {TouchEvent} event
   */
  #didWishlistTouchMove(event) {
    const start = this.#wishlistTouchStart;
    const touch = event.changedTouches[0];
    if (!start || !touch) return false;

    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    return dx > 10 || dy > 10;
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const requestId = (this.#latestCartUpdateRequestIdsByLine.get(line) ?? 0) + 1;
    this.#latestCartUpdateRequestIdsByLine.set(line, requestId);

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (!this.#isLatestCartUpdateRequest(line, requestId)) return;

        resetShimmer(this);

        if (parsedResponseText.errors) {
          const didSyncCartState = this.#syncCartStateFromResponse(parsedResponseText, line);
          this.#handleCartError(line, parsedResponseText, { rollbackCount: !didSyncCartState });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        if (!this.#isLatestCartUpdateRequest(line, requestId)) return;

        this.#rollbackVisibleCartCount(line);
      })
      .finally(() => {
        if (this.#isLatestCartUpdateRequest(line, requestId)) {
          this.#latestCartUpdateRequestIdsByLine.delete(line);
          this.#enableCartItems();
        }

        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (/** @type {DiscountUpdateEvent} */ event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   * @param {{ rollbackCount?: boolean }} [options]
   */
  #handleCartError = (line, parsedResponseText, options = {}) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    if (options.rollbackCount !== false) {
      quantityInput.value = quantityInput.defaultValue;
      this.#updateVisibleCartCount(line, Number(quantityInput.defaultValue) || 0);
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (/** @type {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} */ event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Returns whether a cart update response still belongs to the latest request for its line.
   * @param {number} line
   * @param {number} requestId
   * @returns {boolean}
   */
  #isLatestCartUpdateRequest(line, requestId) {
    return this.#latestCartUpdateRequestIdsByLine.get(line) === requestId;
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Syncs the visible cart count to Shopify's accepted cart state after partial success responses.
   * @param {Object} parsedResponseText
   * @param {Record<string, string>} [parsedResponseText.sections]
   * @param {number} line
   * @returns {boolean}
   */
  #syncCartStateFromResponse(parsedResponseText, line) {
    const sectionHTML = parsedResponseText.sections?.[this.sectionId];
    if (!sectionHTML) return false;

    const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? Number.parseInt(newCartHiddenItemCount, 10) : NaN;

    if (!Number.isFinite(newCartItemCount)) return false;

    const newQuantitySelector = newSectionHTML.querySelectorAll('[ref="quantitySelectors[]"]')[line - 1];
    const newQuantityInput = newQuantitySelector?.querySelector('input');
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (newQuantityInput instanceof HTMLInputElement && quantityInput instanceof HTMLInputElement) {
      quantityInput.value = newQuantityInput.value;
      quantityInput.defaultValue = newQuantityInput.value;
      quantityInput.dataset.optimisticQuantity = newQuantityInput.value;

      if (newQuantityInput.hasAttribute('max')) {
        quantityInput.max = newQuantityInput.max;
      } else {
        quantityInput.removeAttribute('max');
      }

      if (newQuantityInput.hasAttribute('min')) {
        quantityInput.min = newQuantityInput.min;
      }

      if (newQuantityInput.hasAttribute('step')) {
        quantityInput.step = newQuantityInput.step;
      }
    }

    const cartItemCount = this.querySelector('[ref="cartItemCount"]');
    if (cartItemCount instanceof HTMLElement) {
      cartItemCount.textContent = String(newCartItemCount);
    }

    const cartDrawer = this.closest('cart-drawer-component');
    const results = cartDrawer?.querySelector('.cart_results');
    if (results instanceof HTMLElement) {
      results.textContent = `${newCartItemCount} Items`;
    }

    this.#updateCartQuantitySelectorButtonStates();

    return true;
  }

  /**
   * Updates the visible cart count immediately before the cart API response returns.
   * @param {number} line
   * @param {number} nextLineQuantity
   */
  #updateVisibleCartCount(line, nextLineQuantity) {
    const cartItemCount = this.querySelector('[ref="cartItemCount"]');

    if (!(cartItemCount instanceof HTMLElement)) return;

    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const currentTotal = Number.parseInt(cartItemCount.textContent ?? '0', 10);

    if (!(quantityInput instanceof HTMLInputElement)) return;

    const previousLineQuantity = Number.parseInt(
      quantityInput.dataset.optimisticQuantity ?? quantityInput.defaultValue ?? quantityInput.value,
      10
    );

    if (!Number.isFinite(currentTotal) || !Number.isFinite(previousLineQuantity)) return;

    const nextTotal = Math.max(0, currentTotal - previousLineQuantity + nextLineQuantity);
    cartItemCount.textContent = String(nextTotal);
    quantityInput.dataset.optimisticQuantity = String(nextLineQuantity);

    const cartDrawer = this.closest('cart-drawer-component');
    const results = cartDrawer?.querySelector('.cart_results');

    if (results instanceof HTMLElement) {
      results.textContent = `${nextTotal} Items`;
    }
  }

  /**
   * Restores optimistic count changes when the cart request fails before a valid response is returned.
   * @param {number} line
   */
  #rollbackVisibleCartCount(line) {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!(quantityInput instanceof HTMLInputElement)) return;

    quantityInput.value = quantityInput.defaultValue;
    this.#updateVisibleCartCount(line, Number(quantityInput.defaultValue) || 0);
  }

  /**
   * @param {HTMLElement} cartItemRow
   * @returns {Promise<boolean>}
   */
  async #ensureCartDrawerItemInWishlist(cartItemRow) {
    const wishlistButton = cartItemRow.querySelector('.wishlist-product');

    if (!(wishlistButton instanceof HTMLElement)) return false;

    const wishlistApi = /** @type {{ addItemFromElement?: (element: HTMLElement) => Promise<boolean> } | undefined } */ (
      window['__darveysWishlist']
    );
    if (!wishlistApi || typeof wishlistApi.addItemFromElement !== 'function') {
      console.warn('Wishlist API unavailable');
      return false;
    }

    try {
      return await wishlistApi.addItemFromElement(wishlistButton);
    } catch (error) {
      console.warn('Wishlist write failed', error);
      return false;
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
